import React, { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'

import { ErrorDetails } from './ErrorDetails'

export interface ConflictDetailsDrawerProps {
    isOpen: boolean
    onClose: () => void
    title: string
    description: string
    details: string[]
    maxItemsPerSection?: number
    panelMaxWidthClass?: string
    className?: string
}

const DEFAULT_MAX_ITEMS = 9999

export const ConflictDetailsDrawer: React.FC<ConflictDetailsDrawerProps> = ({
    isOpen,
    onClose,
    title,
    description,
    details,
    maxItemsPerSection = DEFAULT_MAX_ITEMS,
    panelMaxWidthClass = 'max-w-md',
    className = 'relative z-[100]',
}) => {
    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className={className} onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div
                        className="fixed inset-0 bg-black/30 dark:bg-black/50"
                        aria-hidden="true"
                    />
                </Transition.Child>

                <div className="fixed inset-0 overflow-hidden">
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                            <Transition.Child
                                as={Fragment}
                                enter="transform transition ease-in-out duration-300"
                                enterFrom="translate-x-full"
                                enterTo="translate-x-0"
                                leave="transform transition ease-in-out duration-300"
                                leaveFrom="translate-x-0"
                                leaveTo="translate-x-full"
                            >
                                <Dialog.Panel
                                    className={`pointer-events-auto flex h-full w-screen flex-col bg-white shadow-xl dark:bg-slate-800 dark:text-white ${panelMaxWidthClass}`}
                                >
                                    <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-600">
                                        <Dialog.Title className="pr-8 text-lg font-semibold text-slate-900 dark:text-white">
                                            {title}
                                        </Dialog.Title>
                                        <button
                                            type="button"
                                            className="rounded-full p-1 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                                            onClick={onClose}
                                            aria-label="Close"
                                        >
                                            <XMarkIcon className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <div className="shrink-0 border-b border-slate-100 px-6 py-3 dark:border-slate-600">
                                        <p className="px-2 py-2 text-sm rounded bg-blue-100 text-blue-700 whitespace-nowrap dark:text-slate-100 dark:bg-slate-700">
                                            {description}
                                        </p>
                                    </div>

                                    <div className="min-h-0 max-h-[calc(100vh-8rem)] flex-1 overflow-y-auto px-6 py-5">
                                        <ErrorDetails
                                            message={description}
                                            details={details}
                                            omitMessage
                                            maxItems={maxItemsPerSection}
                                        />
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
